
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { i18n } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    serviceRequired: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert(i18n.language === 'ar' ? 'الرسالة يجب ألا تتجاوز 500 حرف' : 'Message must not exceed 500 characters');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5qs4bc98t1oshnre12g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          fullName: formData.fullName,
          companyName: formData.companyName,
          email: formData.email,
          serviceRequired: formData.serviceRequired,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          companyName: '',
          email: '',
          serviceRequired: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="py-24 relative">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c0d9f9cd4dbb1a4abb8a88555f3e0b54.jpg"
          alt="Office Meeting Room"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2647]/95 to-[#1A5F7A]/90"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div className="text-white">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-mail-send-line text-2xl text-[#D4AF37]"></i>
              <span className="text-sm font-medium text-gray-200 uppercase tracking-wider">
                {i18n.language === 'ar' ? 'تواصل معنا' : 'Get in Touch'}
              </span>
            </div>

            <h2 className="text-5xl font-bold mb-6 leading-tight">
              <div>{i18n.language === 'ar' ? 'دعنا نساعدك في' : "Let's Help You"}</div>
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] bg-clip-text text-transparent">
                {i18n.language === 'ar' ? 'تحقيق أهدافك التجارية' : 'Achieve Your Goals'}
              </div>
            </h2>

            <p className="text-lg text-gray-200 mb-12 leading-relaxed">
              {i18n.language === 'ar'
                ? 'فريقنا من الخبراء جاهز لمناقشة احتياجاتك التجارية وتقديم حلول مخصصة تناسب أهدافك في السوق العالمية.'
                : 'Our team of experts is ready to discuss your trade requirements and provide customized solutions for your global market objectives.'}
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/20 rounded-full">
                  <i className="ri-map-pin-line text-[#D4AF37] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{i18n.language === 'ar' ? 'مقرنا الرئيسي' : 'Headquarters'}</h4>
                  <p className="text-gray-300 text-sm">{i18n.language === 'ar' ? 'الدوحة، قطر' : 'Doha, Qatar'}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/20 rounded-full">
                  <i className="ri-mail-line text-[#D4AF37] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</h4>
                  <p className="text-gray-300 text-sm">info@kimberlite.qa</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/20 rounded-full">
                  <i className="ri-time-line text-[#D4AF37] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-medium mb-1">{i18n.language === 'ar' ? 'ساعات العمل' : 'Business Hours'}</h4>
                  <p className="text-gray-300 text-sm">
                    {i18n.language === 'ar' ? 'الأحد - الخميس: 8:00 ص - 6:00 م' : 'Sun - Thu: 8:00 AM - 6:00 PM'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-10 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#0A2647] mb-8 text-center">
              {i18n.language === 'ar' ? 'ابدأ محادثة' : 'Start a Conversation'}
            </h3>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'ar' ? 'الاسم الكامل *' : 'Full Name *'}
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A5F7A] focus:border-transparent text-sm"
                    placeholder={i18n.language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                  />
                </div>

                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'ar' ? 'اسم الشركة *' : 'Company Name *'}
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A5F7A] focus:border-transparent text-sm"
                    placeholder={i18n.language === 'ar' ? 'أدخل اسم شركتك' : 'Enter your company name'}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'ar' ? 'البريد الإلكتروني *' : 'Email *'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A5F7A] focus:border-transparent text-sm"
                    placeholder={i18n.language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email address'}
                  />
                </div>

                <div>
                  <label htmlFor="serviceRequired" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'ar' ? 'الخدمة المطلوبة *' : 'Service Required *'}
                  </label>
                  <select
                    id="serviceRequired"
                    name="serviceRequired"
                    value={formData.serviceRequired}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A5F7A] focus:border-transparent text-sm"
                  >
                    <option value="">
                      {i18n.language === 'ar' ? 'اختر الخدمة' : 'Select a service'}
                    </option>
                    <option value="Finding reliable suppliers/buyers">
                      {i18n.language === 'ar' ? 'العثور على موردين/مشترين موثوقين' : 'Finding reliable suppliers/buyers'}
                    </option>
                    <option value="Negotiating the best commercial terms">
                      {i18n.language === 'ar' ? 'التفاوض على أفضل الشروط التجارية' : 'Negotiating the best commercial terms'}
                    </option>
                    <option value="Preparing contracts and monitoring their implementation">
                      {i18n.language === 'ar' ? 'إعداد العقود ومراقبة تنفيذها' : 'Preparing contracts and monitoring their implementation'}
                    </option>
                    <option value="Providing business and market consulting">
                      {i18n.language === 'ar' ? 'تقديم الاستشارات التجارية والسوقية' : 'Providing business and market consulting'}
                    </option>
                    <option value="Facilitating shipping and customs clearance">
                      {i18n.language === 'ar' ? 'تسهيل الشحن والتخليص الجمركي' : 'Facilitating shipping and customs clearance'}
                    </option>
                    <option value="Setting up companies, facilitating banking transactions and opening accounts, finding investors">
                      {i18n.language === 'ar' ? 'تأسيس الشركات وتسهيل المعاملات المصرفية وفتح الحسابات والعثور على المستثمرين' : 'Setting up companies, facilitating banking transactions and opening accounts, finding investors'}
                    </option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {i18n.language === 'ar' ? 'رسالتك *' : 'Your Message *'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A5F7A] focus:border-transparent text-sm resize-none"
                    placeholder={i18n.language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500 {i18n.language === 'ar' ? 'حرف' : 'characters'}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-[#1A5F7A] to-[#0A2647] text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {i18n.language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      {i18n.language === 'ar' ? 'إرسال الرسالة' : 'Send Message'}
                      <i className={`ri-arrow-${i18n.language === 'ar' ? 'left' : 'right'}-line`}></i>
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm flex items-center gap-2">
                      <i className="ri-check-line"></i>
                      {i18n.language === 'ar' ? 'تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.' : 'Your message has been sent successfully! We will contact you soon.'}
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {i18n.language === 'ar' ? 'حدث خطأ في إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'There was an error sending your message. Please try again.'}
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
